import { copy } from '@ohear/shared'
import { Modal } from '@ohear/ui'
/**
 * Page-level keyboard shortcut installer + help modal toggle — Pro Tools
 * keybinding overlay style.
 *
 * The Modal is a PT chrome panel; each row uses .pt-kbd for the keycap chip.
 * Renders nothing visible by itself — installs keydown handlers via
 * `useKeyboardShortcut` and exposes the help modal toggle through the
 * recording store.
 */
import { useCallback } from 'react'
import { useKeyboardShortcut } from '../../lib/use-keyboard'
import { useRecordingStore } from './state'

export interface KeyboardShortcutsProps {
  onPreview: () => void
  onBeginRecord: () => void
  onPickup: () => void
  onAdvance: () => void
  onAbort: () => void
  onAcceptBlock: () => void
  onSceneBlock: () => void
  onEffortBlock: () => void
  onPlayLast: () => void
  locale?: 'zh' | 'en'
}

export function KeyboardShortcuts(props: KeyboardShortcutsProps) {
  const helpOpen = useRecordingStore((s) => s.keyboardHelpOpen)
  const toggleHelp = useRecordingStore((s) => s.toggleKeyboardHelp)
  const locale = props.locale ?? 'en'

  useKeyboardShortcut(' ', (e) => {
    e.preventDefault()
    props.onPreview()
  })
  useKeyboardShortcut('Enter', () => props.onBeginRecord())
  useKeyboardShortcut('r', () => props.onPickup())
  useKeyboardShortcut('n', () => props.onAdvance())
  useKeyboardShortcut('Escape', () => props.onAbort())
  useKeyboardShortcut('b', () => props.onAcceptBlock())
  useKeyboardShortcut('g', () => props.onSceneBlock())
  useKeyboardShortcut('e', () => props.onEffortBlock())
  useKeyboardShortcut('l', () => props.onPlayLast())
  useKeyboardShortcut('?', (e) => {
    e.preventDefault()
    toggleHelp()
  })

  const onClose = useCallback(() => {
    if (helpOpen) toggleHelp()
  }, [helpOpen, toggleHelp])

  return (
    <Modal
      open={helpOpen}
      onClose={onClose}
      title={
        locale === 'zh'
          ? copy.keyboard.KEYBOARD_HELP_HEADER.zh
          : copy.keyboard.KEYBOARD_HELP_HEADER.en
      }
      size="md"
    >
      <div className="rounded-sm border border-pt-border bg-pt-panel2 p-1">
        <ul className="divide-y divide-pt-border-soft">
          {copy.keyboard.SHORTCUTS.map((s) => (
            <li
              key={s.key}
              className="flex items-center justify-between gap-3 px-3 py-2 text-[12px] text-pt-text"
            >
              <span>{locale === 'zh' ? s.label.zh : s.label.en}</span>
              <span className="pt-kbd">{s.key}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-center pt-mono text-[10px] uppercase tracking-[0.18em] text-pt-text-soft">
        {locale === 'zh'
          ? copy.keyboard.KEYBOARD_HELP_FOOTER.zh
          : copy.keyboard.KEYBOARD_HELP_FOOTER.en}
      </p>
    </Modal>
  )
}
